(() => {
  /**
   * ==========================
   * CONFIGURE YOUR RULES HERE
   * ==========================
   *
   * This version assumes:
   * - only ONE addon line should ever exist in cart
   * - addon qty = total qty of all matching parent items
   */


  const FORM_SELECTOR = 'form[action*="/cart/add"]';
  const BOUND_ATTR = 'data-auto-add-bound';

  let isSyncingAddonQty = false;
  let syncQueued = false;
  let initialSyncRan = false;
  let skipNextCartUpdatedSync = false;
  let loaderCount = 0;

  function normalizeValue(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  function getLocaleCartUrl(path) {
    return `${window.Shopify?.routes?.root || '/'}${path}`.replace(/\/{2,}/g, '/');
  }

  function isCartPage() {
    return window.location.pathname.includes('/cart');
  }

  function getPropertyEntries(formData) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      properties[match[1]] = value;
    }

    return properties;
  }

  function lineMatchesRule(lineItem, rule) {
    const props = lineItem.properties || {};
    const value = props[rule.propertyKey];

    if (typeof rule.match === 'function') {
      return rule.match(value, props);
    }

    return normalizeValue(value) === normalizeValue(rule.match);
  }

  function getMatchedRules(properties) {
    return AUTO_ADD_RULES.filter((rule) => {
      const propValue = properties[rule.propertyKey];

      if (typeof rule.match === 'function') {
        return rule.match(propValue, properties);
      }

      return normalizeValue(propValue) === normalizeValue(rule.match);
    });
  }

  function getQualifiedParentRules(lineItem) {
    return AUTO_ADD_RULES.filter((rule) => lineMatchesRule(lineItem, rule));
  }

  function buildMainItem(formData) {
    const properties = getPropertyEntries(formData);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);
    const sellingPlan = formData.get('selling_plan');

    const item = {
      id: Number(variantId),
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    };

    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    if (sellingPlan) {
      item.selling_plan = sellingPlan;
    }

    return item;
  }

  async function addItems(items) {
    const response = await fetch(getLocaleCartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  }

  async function getCart() {
    const response = await fetch(getLocaleCartUrl('cart.js'), {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return response.json();
  }

  async function updateLinesByKey(updates) {
    const response = await fetch(getLocaleCartUrl('cart/update.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  }

  function dispatchImpulseCartRefresh(cart) {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart }
      })
    );
  }

  function dispatchImpulseAddEvent(productPayload) {
    document.dispatchEvent(
      new CustomEvent('ajaxProduct:added', {
        detail: { product: productPayload }
      })
    );
  }

  function disableSubmit(form, disabled) {
    const buttons = form.querySelectorAll('[type="submit"]');

    buttons.forEach((btn) => {
      btn.disabled = disabled;
      btn.classList.toggle('is-loading', disabled);
    });
  }

  function ensureLoaderStyles() {
    if (document.getElementById('addon-cart-loader-styles')) return;

    const style = document.createElement('style');
    style.id = 'addon-cart-loader-styles';
    style.textContent = `
      .addon-cart-loader {
        position: fixed;
        inset: 0;
        background: rgba(255,255,255,0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 99999;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease;
      }

      .addon-cart-loader.is-visible {
        opacity: 1;
        pointer-events: all;
      }

      .addon-cart-loader__spinner {
        width: 42px;
        height: 42px;
        border: 3px solid rgba(0,0,0,0.15);
        border-top-color: rgba(0,0,0,0.75);
        border-radius: 50%;
        animation: addon-cart-loader-spin 0.7s linear infinite;
      }

      @keyframes addon-cart-loader-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureLoaderElement() {
    ensureLoaderStyles();

    let loader = document.getElementById('addon-cart-loader');
    if (loader) return loader;

    loader = document.createElement('div');
    loader.id = 'addon-cart-loader';
    loader.className = 'addon-cart-loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML = `<div class="addon-cart-loader__spinner"></div>`;
    document.body.appendChild(loader);

    return loader;
  }

  function showLoader() {
    loaderCount += 1;
    const loader = ensureLoaderElement();
    loader.classList.add('is-visible');
  }

  function hideLoader() {
    loaderCount = Math.max(0, loaderCount - 1);

    if (loaderCount > 0) return;

    const loader = document.getElementById('addon-cart-loader');
    if (loader) {
      loader.classList.remove('is-visible');
    }
  }

  function isAddonLine(item) {
    const props = item.properties || {};
    return String(props._auto_added || '') === 'true';
  }

  function getParentLines(cart) {
    return (cart?.items || []).filter((item) => !isAddonLine(item));
  }

  function getAddonLinesByVariant(cart, addonVariantId) {
    return (cart?.items || []).filter((item) => {
      return (
        isAddonLine(item) &&
        String(item.variant_id) === String(addonVariantId)
      );
    });
  }

  function getDesiredAddonQuantity(cart, rule) {
    const parentLines = getParentLines(cart);

    return parentLines.reduce((sum, line) => {
      if (lineMatchesRule(line, rule)) {
        return sum + Number(line.quantity || 0);
      }
      return sum;
    }, 0);
  }

  function cartHasAnyQualifiedParent(cart) {
    const parentLines = getParentLines(cart);
    return parentLines.some((line) => getQualifiedParentRules(line).length > 0);
  }

  async function syncAddonQuantities(cart) {
    if (isSyncingAddonQty) {
      syncQueued = true;
      return;
    }

    isSyncingAddonQty = true;
    showLoader();

    try {
      const addonAdds = [];
      const qtyUpdates = {};

      AUTO_ADD_RULES.forEach((rule) => {
        const desiredQty = getDesiredAddonQuantity(cart, rule);
        const addonLines = getAddonLinesByVariant(cart, rule.addonVariantId);

        if (desiredQty <= 0) {
          addonLines.forEach((line) => {
            qtyUpdates[line.key] = 0;
          });
          return;
        }

        if (addonLines.length === 0) {
          addonAdds.push({
            id: Number(rule.addonVariantId),
            quantity: desiredQty,
            properties: {
              _auto_added: 'true',
              _trigger_property: rule.propertyKey
            }
          });
          return;
        }

        const keeper = addonLines[0];

        if (keeper.quantity !== desiredQty) {
          qtyUpdates[keeper.key] = desiredQty;
        }

        addonLines.slice(1).forEach((extra) => {
          qtyUpdates[extra.key] = 0;
        });
      });

      if (addonAdds.length) {
        await addItems(addonAdds);
      }

      if (Object.keys(qtyUpdates).length) {
        await updateLinesByKey(qtyUpdates);
      }

      if (addonAdds.length || Object.keys(qtyUpdates).length) {
        const freshCart = await getCart();
        skipNextCartUpdatedSync = true;
        dispatchImpulseCartRefresh(freshCart);
      }
    } catch (error) {
      console.error('Addon sync failed:', error);
    } finally {
      isSyncingAddonQty = false;
      hideLoader();

      if (syncQueued) {
        syncQueued = false;

        try {
          const latestCart = await getCart();
          await syncAddonQuantities(latestCart);
        } catch (error) {
          console.error('Queued addon sync failed:', error);
        }
      }
    }
  }

  async function handleSubmit(event) {
    const form = event.currentTarget;

    if (!form.checkValidity()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const formData = new FormData(form);
    const mainItem = buildMainItem(formData);
    const matchedRules = getMatchedRules(mainItem.properties || {});

    try {
      showLoader();
      disableSubmit(form, true);

      // First add ONLY the main item
      const addResponse = await addItems([mainItem]);

      // Then fetch cart and sync addon from actual cart state
      const cart = await getCart();

      // Only sync if the product really ended up in cart as a qualifying parent
      if (cartHasAnyQualifiedParent(cart)) {
        await syncAddonQuantities(cart);
      }

      const refreshedCart = await getCart();

      skipNextCartUpdatedSync = true;
      dispatchImpulseAddEvent(addResponse);
      dispatchImpulseCartRefresh(refreshedCart);
    } catch (error) {
      const message =
        error?.description ||
        error?.message ||
        'There was a problem adding this item to the cart.';

      document.dispatchEvent(
        new CustomEvent('ajaxProduct:error', {
          detail: { errorMessage: message }
        })
      );

      console.error('Auto-add failed:', error);
      alert(message);
    } finally {
      disableSubmit(form, false);
      hideLoader();
    }
  }

  function bindForm(form) {
    if (!form || form.hasAttribute(BOUND_ATTR)) return;

    form.setAttribute(BOUND_ATTR, 'true');
    form.addEventListener('submit', handleSubmit, true);
  }

  function bindAllForms(context = document) {
    context.querySelectorAll(FORM_SELECTOR).forEach(bindForm);
  }

  async function runInitialCartSync(force = false) {
    if (initialSyncRan && !force) return;
    initialSyncRan = true;

    try {
      const cart = await getCart();
      await syncAddonQuantities(cart);
    } catch (e) {
      console.error('Initial cart sync failed:', e);
    }
  }

  function bootInitialCartSync() {
    if (isCartPage()) {
      runInitialCartSync();
    }
  }

  document.addEventListener('page:loaded', () => {
    bindAllForms(document);
    bootInitialCartSync();
  });

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    bindAllForms(document);
    bootInitialCartSync();
  }

  document.addEventListener('DOMContentLoaded', bootInitialCartSync);
  window.addEventListener('load', bootInitialCartSync);

  window.addEventListener('pageshow', () => {
    if (isCartPage()) {
      runInitialCartSync(true);
    }
  });

  document.addEventListener('quickview:loaded', (evt) => {
    const productId = evt.detail?.productId;
    if (!productId) return;

    const scope =
      document.querySelector(`#ProductSection-${productId}`) ||
      document.querySelector(`#AddToCartForm-${productId}`)?.closest('form') ||
      document;

    bindAllForms(scope);
  });

  document.addEventListener('quickview:open', (evt) => {
    if (!evt.detail?.initialized) return;

    const productId = evt.detail?.productId;
    if (!productId) return;

    const scope = document.querySelector(`#ProductSection-${productId}`) || document;
    bindAllForms(scope);
  });

  document.addEventListener('cart:updated', async (evt) => {
  const cart = evt.detail?.cart;
  if (!cart) return;

  if (skipNextCartUpdatedSync) {
    skipNextCartUpdatedSync = false;
    return;
  }

  await syncAddonQuantities(cart);
});
})();